import React from 'react';
import { connect } from 'react-redux';
import { reduxForm, getFormValues } from 'redux-form';
import classNames from 'classnames';
import _ from 'lodash';
import compose from 'lodash/fp/compose';
import { withTranslation } from 'react-i18next';

import { MenuEllipsis } from 'jbc-front/components/icons';
import { getExtras, getIsLoading } from 'src/reducers/common/listReducer';
import { REDUCER_NAME, fetchEmployeePays } from 'src/reducers/employees/employeePays';
import ActionSection from 'src/features/EmployeePays/components/ActionSection';
import DetailSearchForm from 'src/components/DetailSearchForm';
import { setItemError } from 'src/reducers/common/itemReducer';
import {
  takePaysSearchQueries,
  takePayConfirmedStateFromSearchForm,
  fetchUniquePaymentDates,
  takePaysSearchQueriesFromLocation,
  takePaysAndAttendancesResetFormQueries,
  paysSearchFormSelector,
  fetchUniquePayrollRuleGroups,
  FORM_NAME_PAYS_SEARCH,
  ITEM_NAME_FETCH_UNIQUE_PAYROLL_RULE_GROUPS,
  isLoading
} from 'src/reducers/searchForm';
import PaymentDateSelectBoxes from 'src/components/PaymentDateSelectBoxes';
import IconActionButton from 'src/components/IconActionButton';
import SearchDetector from 'src/components/SearchDetector';
import Confirmed from 'src/components/Confirmed';
import SearchFormPaginator from 'src/components/SearchFormPaginator';
import PayTargetDateRange from 'src/components/PayTargetDateRange';
import LoadingView from 'src/components/LoadingView';
import ConfirmPayrollContainer from 'src/features/EmployeePays/components/ConfirmPayrollContainer';
import { getSelectOptions } from 'src/reducers/selectOptions';
import withSetupNeeded from 'src/components/withSetupNeeded';
import { INITIAL_POLLING_INTERVAL, MAX_POLLING_INTERVAL } from 'src/constants/Generals';
import ConfirmationPayJobStatus from './components/ConfirmationPayJobStatus';
import DownloadPayCsvJobStatus from './components/DownloadPayCsvJobStatus';
import ImportPayCsvJobStatus from './components/ImportPayCsvJobStatus';
import EmployeePayTable from './containers/EmployeePayTable';
import styles from './EmployeePayList.scss';
import AllRollbackEmployeePayModal from './components/AllRollbackEmployeePayModal';
import EditPaymentDate from './components/EditPaymentDate';
import DashboardSummaryJobStatus from './components/DashboardSummaryJobStatus';

class EmployeePayList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isAllRollbackModalOpen: false,
      bulkModalOpen: false,
      isEditPaymentDateModalOpen: false
    };
  }

  componentDidMount() {
    this.props.dispatch(fetchUniquePayrollRuleGroups(FORM_NAME_PAYS_SEARCH));
    this.pollUpdatePayConfirmationStatus(INITIAL_POLLING_INTERVAL);
  }

  pollUpdatePayConfirmationStatus = interval => {
    const { initialValues: formValues, dispatch } = this.props;
    const { uniquePayrollRuleGroups } = formValues;
    let newInterval = interval;
    if (!_.isEmpty(uniquePayrollRuleGroups)) {
      dispatch(fetchUniquePaymentDates(FORM_NAME_PAYS_SEARCH, uniquePayrollRuleGroups, false, false));
      if (newInterval < MAX_POLLING_INTERVAL) {
        newInterval *= 2;
      }
    }
    setTimeout(this.pollUpdatePayConfirmationStatus, newInterval, newInterval);
  };

  detectingJudgerFnc = (prevProps, nextProps) =>
    nextProps.uniquePaymentDate !== undefined &&
    (prevProps.uniquePaymentDate === undefined || nextProps.location !== prevProps.location);

  openBulkModal = () => {
    this.setState({ bulkModalOpen: true });
  };

  hideBulkModal = () => {
    this.setState({ bulkModalOpen: false });
  };

  openEditPaymentDateModal = () => {
    this.setState({ isEditPaymentDateModalOpen: true });
  };

  hideEditPaymentDateModal = () => {
    this.setState({ isEditPaymentDateModalOpen: false });
  };

  render() {
    const {
      initialValues: formValues,
      uniquePaymentDate,
      location,
      isPayConfirmed,
      resetValues,
      uniquePayrollRuleGroupsCount,
      dispatch,
      extras,
      isListLoading,
      isOptionsLoading,
      clearRollbackError
    } = this.props;
    return (
      <div>
        <SearchDetector
          location={location}
          uniquePaymentDate={uniquePaymentDate}
          willReceivePropsDetectingJudger={this.detectingJudgerFnc}
          onDetect={() => {
            dispatch(fetchEmployeePays(takePaysSearchQueries(formValues)));
          }}
        />
        <div className="l-main-title-wrap">
          <h1 className="m-title-main">{this.props.t('features.EmployeePays.EmployeePayList.salary_list')}</h1>
        </div>
        <LoadingView isLoading={isOptionsLoading}>
          <ConfirmationPayJobStatus />
          <DownloadPayCsvJobStatus />
          <ImportPayCsvJobStatus />
          <DashboardSummaryJobStatus />
          <div className="l-wrap-xl">
            <div
              className={classNames({
                [styles.select]: true,
                [styles.centeredSelect]: uniquePayrollRuleGroupsCount < 2
              })}
            >
              {isPayConfirmed && (
                <div className={styles.confirmed}>
                  <Confirmed />
                </div>
              )}
              <div className={styles.menu}>
                <PaymentDateSelectBoxes formName={FORM_NAME_PAYS_SEARCH} />
                <div className={styles.menuItem}>
                  <IconActionButton
                    icon={<MenuEllipsis size={24} />}
                    options={() => (
                      <ul className={styles.editlist}>
                        <li>
                          <a
                            onClick={() => {
                              this.setState({ isAllRollbackModalOpen: true });
                            }}
                            role="presentation"
                            className={isPayConfirmed ? styles.disabled : ''}
                          >
                            {this.props.t('features.EmployeePays.EmployeePayList.back_to_before_manual_input')}
                          </a>
                        </li>
                        <li>
                          <button className={isPayConfirmed && styles.disabled} onClick={this.openEditPaymentDateModal}>
                            {this.props.t('features.EmployeePays.EmployeePayList.edit_payment_date')}
                          </button>
                        </li>
                      </ul>
                    )}
                  />
                </div>
              </div>
              <ConfirmPayrollContainer
                selectedPayroll={_.get(formValues, 'uniquePayrollRuleGroups')}
                selectedYearMonth={_.get(formValues, 'uniquePaymentDate')}
                isConfirmed={isPayConfirmed}
              />
            </div>
            <PayTargetDateRange formName={FORM_NAME_PAYS_SEARCH} releaseDateVisibility={isPayConfirmed} />
            <DetailSearchForm
              menuRender={() => (
                <ActionSection
                  bulkModalOpen={this.state.bulkModalOpen}
                  openBulkModal={this.openBulkModal}
                  hideBulkModal={this.hideBulkModal}
                  payrollRulesGroups={_.get(formValues, 'uniquePayrollRuleGroups')}
                  yearMonth={_.get(formValues, 'uniquePaymentDate')}
                />
              )}
              formName={FORM_NAME_PAYS_SEARCH}
              resetValues={resetValues}
            />
          </div>
          <LoadingView isLoading={isListLoading}>
            <div className={classNames('l-wrap-xl', styles.list)}>
              <EmployeePayTable formName={FORM_NAME_PAYS_SEARCH} {...extras} />
              <SearchFormPaginator formName={FORM_NAME_PAYS_SEARCH} reducerName={REDUCER_NAME} />
            </div>
          </LoadingView>
        </LoadingView>
        {!isPayConfirmed &&
          this.state.isAllRollbackModalOpen && (
            <AllRollbackEmployeePayModal
              isOpen={this.state.isAllRollbackModalOpen}
              hideModal={() => {
                this.setState({ isAllRollbackModalOpen: false });
                clearRollbackError();
              }}
              payrollRulesGroups={_.get(formValues, 'uniquePayrollRuleGroups')}
              yearMonth={_.get(formValues, 'uniquePaymentDate')}
            />
          )}
        <EditPaymentDate
          isOpen={this.state.isEditPaymentDateModalOpen}
          hideModal={this.hideEditPaymentDateModal}
          selectedPaymentDate={uniquePaymentDate}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  initialValues: {
    ...takePaysSearchQueriesFromLocation(FORM_NAME_PAYS_SEARCH, state.router.location, true),
    ...getFormValues(FORM_NAME_PAYS_SEARCH)(state)
  },
  uniquePaymentDate: paysSearchFormSelector(state, 'uniquePaymentDate'),
  resetValues: takePaysAndAttendancesResetFormQueries(FORM_NAME_PAYS_SEARCH, state, true),
  extras: getExtras(REDUCER_NAME, state),
  location: state.router.location,
  isPayConfirmed: takePayConfirmedStateFromSearchForm(state, FORM_NAME_PAYS_SEARCH),
  uniquePayrollRuleGroupsCount: getSelectOptions(state, 'uniquePayrollRuleGroups').length,
  isListLoading: getIsLoading(REDUCER_NAME, state),
  isOptionsLoading: isLoading(ITEM_NAME_FETCH_UNIQUE_PAYROLL_RULE_GROUPS, state)
});

const mapDispatchToProps = dispatch => ({
  clearRollbackError: () => dispatch(setItemError(REDUCER_NAME, { rollbackEmployeePay: null }))
});
export default withTranslation('translation')(
  compose(
    withSetupNeeded('to_start_payroll_section'),
    connect(mapStateToProps, mapDispatchToProps),
    reduxForm({
      form: FORM_NAME_PAYS_SEARCH,
      enableReinitialize: true
    })
  )(EmployeePayList)
);
